import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import '../RegistrationPage/Register.css'
import { useTranslation } from 'react-i18next'
import { Button, Row, Col, Form, Container, Alert, Spinner } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { getLogin } from '../../entity/user/model/slice/userSlice'

const Login = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  // Form state
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false)

  // Redux state
  const user = useSelector(state => state?.user?.data)
  const userLoading = useSelector(state => state?.user?.loading)
  const userError = useSelector(state => state?.user?.error)

  // Form validation
  const validateForm = () => {
    if (!username.trim()) {
      setError(t('Login.usernameRequired') || 'Username is required')
      return false
    }
    if (!password.trim()) {
      setError(t('Login.passwordRequired') || 'Password is required')
      return false
    }
    if (username.length < 3) {
      setError(t('Login.usernameMinLength') || 'Username must be at least 3 characters')
      return false
    }
    if (password.length < 6) {
      setError(t('Login.passwordMinLength') || 'Password must be at least 6 characters')
      return false
    }
    return true
  }

  const login = async e => {
    e.preventDefault()

    // Clear previous errors
    setError('')

    // Validate form
    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)
      setHasAttemptedLogin(true)

      const loginData = {
        username: username.trim(),
        password: password.trim(),
      }

      // Dispatch login action
      await dispatch(getLogin(loginData))
    } catch (error) {
      console.error('Login error:', error)
      setError(t('Login.networkError') || 'Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle successful login
  useEffect(() => {
    if (user?.accessToken) {
      // Clear form data
      setUsername('')
      setPassword('')
      setError('')
      setHasAttemptedLogin(false)

      // Navigate to home page
      window.location.pathname = '/'
    }
  }, [user?.accessToken])

  // Handle Redux errors
  useEffect(() => {
    if (hasAttemptedLogin && user?.error) {
      let errorMessage = t('Login.error') || 'Login failed'

      // Map different error types to user-friendly messages
      if (typeof user.error === 'string') {
        if (
          user.error.includes('401') ||
          user.error.includes('unauthorized') ||
          user.error.includes('invalid')
        ) {
          errorMessage = t('Login.invalidCredentials') || 'Invalid username or password'
        } else if (user.error.includes('network') || user.error.includes('fetch')) {
          errorMessage = t('Login.networkError') || 'Network error. Please check your connection.'
        } else if (user.error.includes('500')) {
          errorMessage = t('Login.serverError') || 'Server error. Please try again later.'
        }
      }

      setError(errorMessage)
      setIsLoading(false)
    }
  }, [user?.error, hasAttemptedLogin, t])

  // Handle Redux state errors
  useEffect(() => {
    if (hasAttemptedLogin && userError) {
      let errorMessage = t('Login.error') || 'Login failed'

      if (typeof userError === 'string') {
        if (userError.includes('401') || userError.includes('unauthorized')) {
          errorMessage = t('Login.invalidCredentials') || 'Invalid username or password'
        } else if (userError.includes('network') || userError.includes('fetch')) {
          errorMessage = t('Login.networkError') || 'Network error. Please check your connection.'
        } else if (userError.includes('500')) {
          errorMessage = t('Login.serverError') || 'Server error. Please try again later.'
        }
      }

      setError(errorMessage)
    }
  }, [userError, hasAttemptedLogin, t])

  // Clear errors when user starts typing
  useEffect(() => {
    if (error && (username || password)) {
      const timer = setTimeout(() => {
        setError('')
      }, 3000) // Clear error after 3 seconds of typing

      return () => clearTimeout(timer)
    }
  }, [username, password, error])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setUsername('')
      setPassword('')
      setError('')
      setHasAttemptedLogin(false)
      setIsLoading(false)
    }
  }, [])

  // Determine if form should be disabled
  const isFormDisabled = isLoading || userLoading

  function ErrorMessage() {
    if (!error) return null

    return (
      <div className='alert'>
        <Alert variant='danger' dismissible onClose={() => setError('')}>
          <p style={{ margin: 0 }}>{error}</p>
        </Alert>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <meta charSet='utf-8' />
        <title>{t('Login.authorization')}</title>
        <link rel='canonical' href='http://moambe.org.ge/login' />
        <meta
          name='description'
          content={
            t('Login.metaDescription') ||
            'Login to access your moambe account - მოამბე, archive, მოამბე, არქივი'
          }
        />
        <meta name='keywords' content='moambe, archive, მოამბე, არქივი, login, authorization' />
      </Helmet>
      <Container>
        <Row className='flex items-center justify-center flex-col mt-32 p-2'>
          <h4>{t('Login.authorization')}</h4>
          {error && <ErrorMessage />}
          <Col md='5' className='login-form'>
            <Form className='register-form' onSubmit={login} noValidate>
              <Row>
                <Col className='p-0'>
                  <Form.Group controlId='loginUsername'>
                    <Form.Control
                      type='text'
                      placeholder={t('Login.username')}
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      disabled={isFormDisabled}
                      required
                      autoComplete='username'
                      aria-describedby={error ? 'login-error' : undefined}
                      isInvalid={error && !username.trim()}
                    />
                  </Form.Group>
                  <Form.Group controlId='loginPassword'>
                    <Form.Control
                      type='password'
                      placeholder={t('Login.password')}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isFormDisabled}
                      required
                      autoComplete='current-password'
                      aria-describedby={error ? 'login-error' : undefined}
                      isInvalid={error && !password.trim()}
                    />
                  </Form.Group>
                  <Button
                    variant='primary'
                    type='submit'
                    disabled={isFormDisabled || !username.trim() || !password.trim()}
                    className='register-page btn'
                  >
                    {isFormDisabled ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          style={{ marginRight: '0.5rem' }}
                        />
                        {t('Login.loggingIn') || 'Logging in...'}
                      </>
                    ) : (
                      t('Login.loginbutton')
                    )}
                  </Button>
                </Col>
              </Row>
              <Row>
                <div className='mr-auto py-3'>
                  <Link to='/register' tabIndex={isFormDisabled ? -1 : 0}>
                    {t('Login.regbutton')}
                  </Link>
                </div>
                <div className='py-3'>
                  <Link to='/reset' tabIndex={isFormDisabled ? -1 : 0}>
                    {t('Login.forgotpassword')}
                  </Link>
                </div>
              </Row>
            </Form>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Login
